import React, { useState, useCallback, useMemo } from "react";
import { differenceInYears, parseISO } from "date-fns";
import { useQuestionnaire } from "../../../context/questionnaire";
import { RendererProps } from "./index";
import { TextInput } from "./renderer-styles";

export function getAgeFromDob(dobIso: string | undefined): number | null {
  if (!dobIso) return null;
  try {
    const dob = parseISO(dobIso);
    const age = differenceInYears(new Date(), dob);
    return age >= 0 ? age : null;
  } catch {
    return null;
  }
}

const NumberInputRenderer: React.FC<RendererProps> = ({
  question,
  answer,
  onAnswer,
}) => {
  const { answers } = useQuestionnaire();

  const effectiveMax = useMemo(() => {
    if (question.max_value_is_age) {
      const patientAge = getAgeFromDob(answers["date_of_birth"]?.value);
      if (patientAge != null && question.max == null) return patientAge;
      if (patientAge != null && question.max != null)
        return Math.min(question.max, patientAge);
    }
    return question.max ?? undefined;
  }, [answers, question.max, question.max_value_is_age]);

  const [value, setValue] = useState<string>(
    answer?.value != null ? String(answer.value) : ""
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      setValue(raw);

      if (raw === "") {
        onAnswer("", !question.required);
        return;
      }

      const num = Number(raw);
      const inRange =
        (question.min == null || num >= question.min) &&
        (effectiveMax == null || num <= effectiveMax);
      onAnswer(num, !isNaN(num) && inRange);
    },
    [onAnswer, question.required, question.min, effectiveMax]
  );

  return (
    <TextInput
      type="number"
      value={value}
      onChange={handleChange}
      placeholder={question.placeholder || ""}
      min={question.min}
      max={effectiveMax}
    />
  );
};

export function getNumberErrorMessage(
  min: number | undefined,
  max: number | undefined
): string {
  if (min != null && max != null)
    return `Please enter a value between ${min} and ${max}`;
  if (max != null) return `Please enter a value between 0 and ${max}`;
  if (min != null) return `Please enter a value of at least ${min}`;
  return "Please enter a valid number";
}

export default NumberInputRenderer;
